import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import {
  PhoneCallService,
  type PhoneCallTemplateDto,
  type PhoneCallTemplateRequest,
  type TemplateTokenDto,
  type TemplateType,
  type UpdatePhoneCallTemplateRequest,
} from '@/services/api';

const reminderCallSettingsKeys = {
  getReminderCallSettingsTemplatesList: () =>
    ['getReminderCallSettingsTemplatesList'] as const,
  getReminderCallTemplateTokens: (
    params: GetReminderCallTemplateTokensQueryParams
  ) => ['getSmsTemplateTokens', params] as const,
  getSingleReminderCallTemplate: (reminderCallTemplateId: string) =>
    ['getSingleSmsTemplate', reminderCallTemplateId] as const,
};

export const useGetReminderCallSettingsTemplatesList = (
  options?: Partial<UseQueryOptions<Array<PhoneCallTemplateDto>, AxiosError>>
) => {
  return useQuery<Array<PhoneCallTemplateDto>, AxiosError>({
    queryKey: reminderCallSettingsKeys.getReminderCallSettingsTemplatesList(),
    queryFn: () => PhoneCallService.getPhoneCallTemplate(),
    ...options,
  });
};

export const useDeletePhoneCallTemplateMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, { templateId: string }>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { templateId: string }>({
    mutationFn: ({ templateId }) =>
      PhoneCallService.deletePhoneCallTemplateById({ id: templateId }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey:
          reminderCallSettingsKeys.getReminderCallSettingsTemplatesList(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type GetReminderCallTemplateTokensQueryParams = {
  type: TemplateType;
};

export const useGetReminderCallTemplateTokens = (
  params: GetReminderCallTemplateTokensQueryParams,
  options?: Partial<UseQueryOptions<Array<TemplateTokenDto>, AxiosError>>
) => {
  return useQuery<Array<TemplateTokenDto>, AxiosError>({
    queryKey: reminderCallSettingsKeys.getReminderCallTemplateTokens(params),
    queryFn: () =>
      PhoneCallService.getPhoneCallTemplateToken(
        params,
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type GetReminderCallTemplateQueryParams = {
  id: string;
};

export const useGetReminderCallTemplateById = (
  params: GetReminderCallTemplateQueryParams,
  options?: Partial<UseQueryOptions<PhoneCallTemplateDto, AxiosError>>
) => {
  return useQuery<PhoneCallTemplateDto, AxiosError>({
    queryKey: reminderCallSettingsKeys.getSingleReminderCallTemplate(params.id),
    queryFn: () =>
      PhoneCallService.getAllPhoneCallTemplate(
        params,
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type editReminderCall = {
  id: string;
  body?: UpdatePhoneCallTemplateRequest;
};

export const useEditReminderCallTemplateMutation = (
  options?: UseMutationOptions<unknown, AxiosError, editReminderCall>
) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, editReminderCall>({
    mutationFn: async (req) => {
      return PhoneCallService.putAllPhoneCallTemplate(req);
    },
    ...options,
    async onSuccess(data, variables, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey:
            reminderCallSettingsKeys.getReminderCallSettingsTemplatesList(),
        }),
        queryClient.invalidateQueries({
          queryKey: reminderCallSettingsKeys.getSingleReminderCallTemplate(
            variables.id
          ),
        }),
      ]);

      options?.onSuccess?.(data, variables, context);
    },
  });
};

type CreatePhoneCallReminderTemplate = {
  body?: PhoneCallTemplateRequest;
};

export const useCreateSmsTemplateMutation = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    CreatePhoneCallReminderTemplate
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, CreatePhoneCallReminderTemplate>({
    mutationFn: async (body) => {
      return PhoneCallService.postPhoneCallTemplate(body);
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey:
          reminderCallSettingsKeys.getReminderCallSettingsTemplatesList(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
